import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '../../../contexts/TenantContext';
import {
  createInMemorySurfaceMemoryStore,
  type BusinessWorkspaceState,
  type SurfaceMemoryStore,
} from '@shared/business-workbench/workbench-workspace-state';
import { deriveWorkbenchFocusFromWorkspaceState } from '@shared/business-workbench/workbench-focus-adapter';
import { parseWorkbenchLocation } from '@shared/business-workbench/workbench-route-state';
import { restoreListWorkspaceState } from '@shared/business-workbench/workbench-surface-memory-restore';
import { setActiveWorkbenchCommandFocus } from '../../../workbench/workbench-command-focus-bridge';

export interface BusinessWorkbenchRuntimeValue {
  tenantId: string;
  runtimeInstanceId: string;
  workspaceState: BusinessWorkspaceState;
  surfaceMemory: SurfaceMemoryStore;
  patchWorkspaceState: (patch: Partial<BusinessWorkspaceState>) => void;
  setSelection: (selection: BusinessWorkspaceState['selection']) => void;
}

const BusinessWorkbenchRuntimeContext = createContext<BusinessWorkbenchRuntimeValue | null>(null);

function createRuntimeInstanceId(): string {
  return `bwrt-${Math.random().toString(36).slice(2, 10)}`;
}

function applyWorkspacePatch(
  current: BusinessWorkspaceState,
  patch: Partial<BusinessWorkspaceState>
): BusinessWorkspaceState {
  const next = { ...current, ...patch };
  if (next.surface === 'list') {
    const { root: _root, ...withoutRoot } = next;
    return withoutRoot;
  }
  return next;
}

export interface BusinessWorkbenchRuntimeProviderProps {
  children: React.ReactNode;
}

export function BusinessWorkbenchRuntimeProvider({
  children,
}: BusinessWorkbenchRuntimeProviderProps): React.ReactElement {
  const location = useLocation();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id ?? 'default_tenant';
  const runtimeInstanceIdRef = useRef<string>(createRuntimeInstanceId());
  const surfaceMemoryRef = useRef<SurfaceMemoryStore>(createInMemorySurfaceMemoryStore());
  const [workspaceState, setWorkspaceState] = useState<BusinessWorkspaceState>(() =>
    parseWorkbenchLocation(location.pathname, location.search)
  );

  const patchWorkspaceState = useCallback((patch: Partial<BusinessWorkspaceState>) => {
    setWorkspaceState((current) => applyWorkspacePatch(current, patch));
  }, []);

  const setSelection = useCallback((selection: BusinessWorkspaceState['selection']) => {
    setWorkspaceState((current) => applyWorkspacePatch(current, { selection: [...selection] }));
  }, []);

  const value = useMemo<BusinessWorkbenchRuntimeValue>(
    () => ({
      tenantId,
      runtimeInstanceId: runtimeInstanceIdRef.current,
      workspaceState,
      surfaceMemory: surfaceMemoryRef.current,
      patchWorkspaceState,
      setSelection,
    }),
    [tenantId, workspaceState, patchWorkspaceState, setSelection]
  );

  useEffect(() => {
    setWorkspaceState((current) => {
      const parsed = parseWorkbenchLocation(location.pathname, location.search);
      const nextState =
        parsed.surface === 'list'
          ? restoreListWorkspaceState(parsed, surfaceMemoryRef.current)
          : parsed;
      if (
        current.surface === nextState.surface &&
        current.domain === nextState.domain &&
        current.subdomain === nextState.subdomain &&
        current.root?.id === nextState.root?.id &&
        current.root?.businessDomain === nextState.root?.businessDomain &&
        current.selection.length === nextState.selection.length &&
        current.selection.every(
          (ref, index) =>
            ref.id === nextState.selection[index]?.id &&
            ref.businessDomain === nextState.selection[index]?.businessDomain &&
            ref.label === nextState.selection[index]?.label
        )
      ) {
        return current;
      }
      return nextState;
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    setActiveWorkbenchCommandFocus(
      deriveWorkbenchFocusFromWorkspaceState(workspaceState, {
        actionProjection: 'commandPalette',
        variant: 'object',
      })
    );

    return () => {
      setActiveWorkbenchCommandFocus(null);
    };
  }, [workspaceState]);

  return (
    <BusinessWorkbenchRuntimeContext.Provider value={value}>
      {children}
    </BusinessWorkbenchRuntimeContext.Provider>
  );
}

export function useBusinessWorkbenchRuntime(): BusinessWorkbenchRuntimeValue {
  const ctx = useContext(BusinessWorkbenchRuntimeContext);
  if (!ctx) {
    throw new Error(
      'useBusinessWorkbenchRuntime must be used within BusinessWorkbenchRuntimeProvider'
    );
  }
  return ctx;
}

export function useOptionalBusinessWorkbenchRuntime(): BusinessWorkbenchRuntimeValue | null {
  return useContext(BusinessWorkbenchRuntimeContext);
}
